import React, { ReactElement, useEffect } from 'react';
import Invoice from './Invoice';
import usePersistedState from 'shared/hooks/usePersistedState';
import { PersistedStateKeys } from 'shared/enums';
import { Modal, ModalCloseButton, ModalBody } from '@televet/kibble-ui/build/components/Modal';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { toggleInvoicePreviewModal } from 'pages/Conversations/state/conversationsSlice';

interface IInvoicePreviewModalProps {
  attachmentInvoiceIds: string[];
  defaultServiceFeePercentage: number;
  currentInvoiceId?: string;
  clinicPetParentId: string;
  attachmentFeePercent: number | undefined;
  invoiceAttachmentTotal: string;
  updateInvoiceAttachmentDiscount: (invoiceId: string, discount: number) => void;
}

const InvoicePreviewModal = ({
  attachmentInvoiceIds,
  defaultServiceFeePercentage,
  currentInvoiceId,
  clinicPetParentId,
  attachmentFeePercent,
  invoiceAttachmentTotal,
  updateInvoiceAttachmentDiscount,
}: IInvoicePreviewModalProps): ReactElement | null => {
  const { isInvoicePreviewModalOpen: isOpen } = useAppSelector((state) => state.conversations.channelViewData);
  const dispatch = useAppDispatch();
  const [isDiscoveredInvoiceDemoModeEnabled] = usePersistedState(
    PersistedStateKeys.IsDiscoveredInvoiceDemoModeEnabled,
    false,
  );

  const onClose = (): void => {
    dispatch(toggleInvoicePreviewModal({ isOpen: false }));
  };

  useEffect(() => {
    if (!isOpen) {
      const printWindow = document.getElementById('printWindow');
      if (printWindow) {
        document.body.removeChild(printWindow);
      }
    }
  }, [isOpen]);

  if (attachmentInvoiceIds.length > 0 || currentInvoiceId || isDiscoveredInvoiceDemoModeEnabled) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalCloseButton />
        <ModalBody p={0} maxH="90vh">
          {/* If currentInvoiceId exists, modal is being opened from message bubble, so use that first */}
          {currentInvoiceId ? (
            <Invoice
              key={currentInvoiceId}
              invoiceId={currentInvoiceId}
              clinicPetParentId={clinicPetParentId}
              defaultServiceFeePercentage={defaultServiceFeePercentage}
            />
          ) : attachmentInvoiceIds.length > 0 ? (
            // in message draft, show multiple invoices in same preview modal
            attachmentInvoiceIds.map((invoiceId) => {
              return (
                <Invoice
                  key={invoiceId}
                  invoiceId={invoiceId}
                  clinicPetParentId={clinicPetParentId}
                  defaultServiceFeePercentage={defaultServiceFeePercentage}
                  attachmentFeePercent={attachmentFeePercent}
                  invoiceAttachmentTotal={invoiceAttachmentTotal}
                  updateInvoiceAttachmentDiscount={updateInvoiceAttachmentDiscount}
                />
              );
            })
          ) : isDiscoveredInvoiceDemoModeEnabled ? (
            <Invoice
              invoiceId=""
              clinicPetParentId=""
              defaultServiceFeePercentage={defaultServiceFeePercentage}
              attachmentFeePercent={attachmentFeePercent}
              invoiceAttachmentTotal={invoiceAttachmentTotal}
            />
          ) : null}
        </ModalBody>
      </Modal>
    );
  }
  return null;
};

export default InvoicePreviewModal;
